/**
 * Room — the live lobby experience. Renders the seat grid (waiting),
 * the GameStage (playing), the PostRoundPane (roundEnd), and the
 * FinalStandings (finalStandings). Settings panel is always one tap away
 * via the gear icon.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { Character, LobbyTicket } from '../../lib/api';
import { listGames, getCharacterStats, type GameTile } from '../../lib/api';
import { IconAvatar } from '../characters/IconAvatar';
import { useLobbySocket, clearRejoin } from './useLobbySocket';
import { GameStage } from '../game/GameStage';
import { UniversalMatchIntro } from '../game/UniversalMatchIntro';
import { enterGameFullscreen, exitGameFullscreen, lockOrientation, onFullscreenChange, isFullscreen, isIos, fullscreenSupported } from '../game/fullscreen';
import { RotateOverlay } from '../game/RotateOverlay';
import { IosInstallNudge } from '../game/IosInstallNudge';
import { gameOrientation, gameName } from '../game/manifests';
import { SettingsPanel } from './SettingsPanel';
import { PostRoundPane } from './PostRoundPane';
import { MatchStartVotePane } from './MatchStartVotePane';
import { FinalStandings } from './FinalStandings';
import { FreeModeGrid } from './FreeModeGrid';
import { gameAvailability } from './gameAvailability';
import { feedbackPrefs } from '@tt/feedback';
import { audio } from '@tt/audio';
import { MeDrawer } from '../me/MeDrawer';
import { XPBar } from '../me/XPBar';
import { BellButton } from '../me/BellButton';
import { DailyChallengeWidget } from '../me/DailyChallengeWidget';
import { ToastQueue, useToastQueue } from '../me/UnlockToast';
import { Settings as SettingsIcon, Volume2, VolumeX, Smile, Link2, LogOut, Flame, Star } from 'lucide-react';
import { featuredGameForWeek, FEATURED_XP_MULTIPLIER } from '@tt/lobby/season';
import { TavernBackdrop } from './TavernBackdrop';
import { TavernFloor } from './TavernFloor';
import { titleRarityClass } from '../me/titleRarity';
import { CharacterProfileModal } from '../characters/CharacterProfileModal';
import type { RoundXp } from '@tt/lobby';
import { track } from '../../lib/analytics';

interface Props {
  character: Character;
  ticket: LobbyTicket;
  onLeave: () => void;
  onCharacterUpdated: (character: Character) => void;
  initialSeq?: number;
}

const REACTION_PICKER_BASE = ['👏', '🔥', '💀', '🎉', '😂', '😱'];
const REACTION_PICKER_UNLOCKABLE = ['🏆', '⚡', '🎲', '💎', '🍀', '🎭'];

export function Room({ character, ticket, onLeave, onCharacterUpdated, initialSeq }: Props) {
  const sock = useLobbySocket({
    wsUrl: ticket.wsUrl,
    ticket: ticket.ticket,
    enabled: true,
    charId: character.charId,
    sinceSeq: initialSeq,
  });

  const [games, setGames] = useState<GameTile[]>([]);
  const [chatDraft, setChatDraft] = useState('');
  const [copied, setCopied] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [openSettingsAfterReset, setOpenSettingsAfterReset] = useState(false);
  const [reactionPicker, setReactionPicker] = useState(false);
  const [meOpen, setMeOpen] = useState(false);
  const [profileCharId, setProfileCharId] = useState<string | null>(null);
  const [soundOn, setSoundOn] = useState(!audio.prefs.get().muted);
  const [unlockedEmotes, setUnlockedEmotes] = useState<string[]>([]);
  const [dailyStreak, setDailyStreak] = useState<number>(0);
  /** Phase D — lantern balance pip in the Room header. Initialized from
   *  the cached character row (so it shows immediately on first paint),
   *  refreshed from /stats once the initial fetch lands and again at
   *  every roundEnd / finalStandings transition (via the same stats
   *  refresh path the unlock toaster uses). */
  const [lanternBalance, setLanternBalance] = useState<number>(character.lanterns ?? 0);
  const [xpRefreshKey, setXpRefreshKey] = useState(0);
  const [xpBoost, setXpBoost] = useState(false);
  const [xpToast, setXpToast] = useState<{ matchId: string; xp: RoundXp } | null>(null);
  const toastQueue = useToastQueue();
  const lastToastedMatchRef = useRef<string | null>(null);
  const prevUnlockedRef = useRef<Set<string> | null>(null);
  useEffect(() => {
    void (async () => {
      try {
        const s = await getCharacterStats(character.charId);
        setUnlockedEmotes(s.inventory.emotes ?? []);
        setDailyStreak(s.dailyStreak ?? 0);
        if (typeof s.lanterns === 'number') setLanternBalance(s.lanterns);
        // Seed the unlocked-id snapshot so we don't toast existing inventory.
        prevUnlockedRef.current = collectUnlockedIds(s.inventory.unlockHints ?? []);
      } catch { /* */ }
    })();
  }, [character.charId]);

  const chatEndRef = useRef<HTMLDivElement | null>(null);

  // ---- Fullscreen takeover (Total-Redesign-Spec §1b, wave 0) ----
  // Entry happens inside the Ready/Start CLICK handlers below (transient
  // activation required); this block handles exit + orientation lock +
  // the ⛶ re-enter pill's "are we fullscreen?" state.
  const [fsActive, setFsActive] = useState<boolean>(() => isFullscreen());
  useEffect(() => onFullscreenChange(() => setFsActive(isFullscreen())), []);
  const lobbyState = sock.lobby?.state;
  const prevLobbyStateRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const prev = prevLobbyStateRef.current;
    prevLobbyStateRef.current = lobbyState;
    // Leave takeover when a round ends (playing → anything else). We do
    // NOT exit on waiting→voting etc. so fullscreen entered at Ready
    // survives the vote screen into the match.
    if (prev === 'playing' && lobbyState !== 'playing') {
      void exitGameFullscreen();
    }
  }, [lobbyState]);
  // Landscape lock for orientation-declared games — only valid while
  // fullscreen, and needs no gesture, so it can run when the match starts.
  const playingGameId = lobbyState === 'playing' ? sock.currentMatch?.gameId : undefined;
  useEffect(() => {
    if (!playingGameId) return;
    const o = gameOrientation(playingGameId);
    if (o === 'landscape') void lockOrientation('landscape');
  }, [playingGameId]);
  // Unmount (user leaves mid-game): drop takeover.
  useEffect(() => () => { void exitGameFullscreen(); }, []);

  useEffect(() => {
    void (async () => {
      try { setGames((await listGames()).games); } catch { /* */ }
    })();
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ block: 'end' });
  }, [sock.chat.length]);

  const lobby = sock.lobby;
  const me = useMemo(() => lobby?.players.find((p) => p.charId === character.charId) ?? null, [lobby, character.charId]);
  const myRoundXp = sock.lastResult?.xp?.[character.charId] ?? null;
  useEffect(() => {
    if (lobby?.state === 'roundEnd' || lobby?.state === 'finalStandings') {
      setXpRefreshKey((k) => k + 1);
      setXpBoost(true);
      const t = window.setTimeout(() => setXpBoost(false), 1800);
      return () => window.clearTimeout(t);
    }
    return;
  }, [lobby?.state]);

  // Phase 4: bump refreshKey ALSO when a new round result arrives
  // (sock.lastResult). Server-side daily challenge progress is updated
  // inside finalizeMatch; this gives the widget + activity feed a
  // second push to refetch the moment the result lands, instead of
  // waiting for the lobby state machine to flip. The widget's existing
  // refreshKey-driven fetch is the auth source — local deltas would
  // need a server-confirm path that doesn't exist yet (deferred to a
  // future store-based rewrite, see customization-updates.md §6).
  useEffect(() => {
    if (sock.lastResult?.matchId) setXpRefreshKey((k) => k + 1);
  }, [sock.lastResult?.matchId]);

  // Analytics: fire match_start once per matchId, match_end once per result.
  useEffect(() => {
    if (!sock.currentMatch?.matchId) return;
    track('match_start', {
      matchId: sock.currentMatch.matchId,
      gameId: sock.currentMatch.gameId,
      players: lobby?.players.filter((p) => !p.isSpectator).length ?? 0,
    });
  }, [sock.currentMatch?.matchId]);
  useEffect(() => {
    if (!sock.lastResult?.matchId) return;
    track('match_end', {
      matchId: sock.lastResult.matchId,
      gameId: sock.lastResult.gameId,
    });
  }, [sock.lastResult?.matchId]);

  // Bump on a reaction send, since react-5 daily challenge ticks per
  // reaction. Server already knows; this just nudges the widget to
  // refetch within ~200ms instead of waiting for the next round end.
  useEffect(() => {
    const r = sock.reactions[sock.reactions.length - 1];
    if (!r) return;
    const t = window.setTimeout(() => setXpRefreshKey((k) => k + 1), 250);
    return () => window.clearTimeout(t);
  }, [sock.reactions.length]);

  // Detect newly-unlocked cosmetics on round/finalStandings transitions.
  useEffect(() => {
    if (lobby?.state !== 'roundEnd' && lobby?.state !== 'finalStandings') return;
    let live = true;
    void (async () => {
      try {
        const s = await getCharacterStats(character.charId);
        if (typeof s.lanterns === 'number') setLanternBalance(s.lanterns);
        const hints = s.inventory.unlockHints ?? [];
        const cur = collectUnlockedIds(hints);
        const prev = prevUnlockedRef.current;
        prevUnlockedRef.current = cur;
        if (!live || !prev) return;
        const newly: UnlockHint[] = [];
        for (const h of hints) {
          if (!h.unlocked) continue;
          const key = `${h.kind}:${h.id}`;
          if (!prev.has(key)) newly.push(h);
        }
        for (const h of newly.slice(0, 6)) {
          toastQueue.push({
            tier: 2,
            rarity: rarityForUnlockKind(h.kind),
            kind: 'cosmetic',
            title: h.label,
            subtitle: cosmeticSubtitle(h.kind),
            rewardChip: cosmeticChip(h.kind),
            icon: cosmeticIcon(h.kind),
          });
        }
        setUnlockedEmotes(s.inventory.emotes ?? []);
      } catch { /* */ }
    })();
    return () => { live = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lobby?.state, character.charId]);
  useEffect(() => {
    const result = sock.lastResult;
    if (!result?.matchId) return;
    if (lastToastedMatchRef.current === result.matchId) return;
    lastToastedMatchRef.current = result.matchId;

    const xp = result.xp?.[character.charId];
    let legacyTimer: number | null = null;
    if (xp && xp.total > 0) {
      setXpToast({ matchId: result.matchId, xp });
      legacyTimer = window.setTimeout(() => {
        setXpToast((cur) => cur?.matchId === result.matchId ? null : cur);
      }, 4200);
      toastQueue.push({
        tier: 1,
        rarity: 1,
        kind: 'currency',
        title: `+${xp.total} XP`,
        subtitle: xp.items.slice(0, 2).map((it) => `${it.label} +${it.amount}`).join(' · '),
        rewardChip: `Lv ${xp.levelAfter}`,
        icon: '+',
      });
      if (xp.levelAfter > xp.levelBefore) {
        const isMilestone = xp.levelAfter % 5 === 0;
        toastQueue.push({
          tier: isMilestone ? 3 : 2,
          rarity: isMilestone ? 4 : 3,
          kind: 'level',
          title: `Level ${xp.levelAfter}`,
          subtitle: xp.levelAfter - xp.levelBefore > 1
            ? `${xp.levelBefore} → ${xp.levelAfter}`
            : 'Account level up',
          ...(isMilestone ? { rewardChip: 'Milestone' } : {}),
          icon: '⬆',
        });
      }
    }
    const myAwards = (result.awards ?? []).filter((a) => a.charId === character.charId);
    for (const a of myAwards) {
      if (a.label.startsWith('🏅')) {
        const name = a.label.replace(/^🏅\s*/, '').trim();
        toastQueue.push({
          tier: 2,
          rarity: 3,
          kind: 'achievement',
          title: name,
          subtitle: 'Achievement earned',
          rewardChip: 'Badge',
          icon: '★',
        });
      }
    }
    return () => {
      if (legacyTimer !== null) window.clearTimeout(legacyTimer);
    };
  }, [sock.lastResult?.matchId, character.charId]);
  const isHost = !!me?.isHost;
  const livePlayers = lobby?.players.filter((p) => !p.isSpectator) ?? [];
  const allowedGameIds = lobby?.settings.allowedGames ?? [];
  // R1#17: respect baseGameId when allowedGames stores the base id so
  // separate-variant tiles aren't dropped from the eligibility count.
  // Mirrors PostRoundPane.tsx's HostPickSection predicate.
  const eligibleGameCount = lobby
    ? games.filter((g) =>
        (!allowedGameIds.length
          || allowedGameIds.includes(g.id)
          || (g.baseGameId ? allowedGameIds.includes(g.baseGameId) : false))
        && gameAvailability(g, livePlayers.length).playable,
      ).length
    : 0;
  const readyToStart = !!lobby && livePlayers.length >= 2 && lobby.players.every((p) => p.ready || p.isSpectator);
  const allReady = readyToStart && eligibleGameCount > 0;
  const startBlockedReason = livePlayers.length < 2
    ? 'Need 2+ players'
    : eligibleGameCount === 0
      ? 'No selected games work for this player count'
      : 'Need everyone ready';
  const inviteUrl = useMemo(() => `${window.location.origin}/r/${ticket.roomCode}`, [ticket.roomCode]);

  useEffect(() => {
    if (openSettingsAfterReset && lobby?.state === 'waiting') {
      setSettingsOpen(true);
      setOpenSettingsAfterReset(false);
    }
  }, [openSettingsAfterReset, lobby?.state]);

  function copy(text: string) {
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    }).catch(() => {});
  }

  // Connecting timeout — single-shot from mount. If lobby.state hasn't
  // arrived in 12s, force a leave back to home + clear rejoin. Refs are
  // used so the effect runs ONCE on mount (depending on sock.lobby/
  // sock.connected/onLeave caused this to reset the timer every render
  // and never fire). Without this, "host closes lobby + players refresh"
  // could leave players stuck on a black/connecting screen forever.
  const lobbyArrivedRef = useRef(false);
  const onLeaveRef = useRef(onLeave);
  onLeaveRef.current = onLeave;
  useEffect(() => {
    if (sock.lobby) lobbyArrivedRef.current = true;
  }, [sock.lobby]);
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (!lobbyArrivedRef.current) {
        // eslint-disable-next-line no-console
        console.warn('[Room] connection stalled — falling back to home');
        try { clearRejoin(); } catch { /* */ }
        onLeaveRef.current();
      }
    }, 12_000);
    return () => window.clearTimeout(t);
  }, []);

  // Always-visible emergency leave button. If anything below fails to
  // render (lobby never arrives, host closed the lobby into a weird
  // state, server hiccup), the user can ALWAYS click here to escape
  // the black-screen trap. Floats over everything via fixed position.
  // Fires the same path as the chrome Leave button.
  const escapeButton = (
    <button
      type="button"
      onClick={() => {
        try { clearRejoin(); } catch { /* */ }
        try { sessionStorage.removeItem('tt:nocon-reloads'); } catch { /* */ }
        sock.leave();
        onLeave();
      }}
      style={{
        position: 'fixed',
        top: 8,
        right: 8,
        zIndex: 9999,
        padding: '8px 14px',
        background: 'rgba(20, 8, 10, 0.85)',
        color: '#fff2c8',
        border: '1px solid #ffd770',
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
      }}
      aria-label="Leave room"
    >
      Leave
    </button>
  );

  if (sock.kicked) {
    return (
      <>
        {escapeButton}
        <main className="full-center">
          <div className="welcome-card" style={{ textAlign: 'center' }}>
            <h1 className="display" style={{ margin: 0 }}>You were removed.</h1>
            <p className="muted">Reason: {sock.kicked}</p>
            <button type="button" className="btn primary" onClick={onLeave}>Back home</button>
          </div>
        </main>
      </>
    );
  }

  if (sock.finalStandings) {
    return (
      <>
        {escapeButton}
        <FinalStandings
          lobby={lobby!}
          finalStandings={sock.finalStandings}
          isHost={isHost}
          myCharId={character.charId}
          lastRoundXp={myRoundXp}
          xpRefreshKey={xpRefreshKey}
          onNewSession={() => sock.newSession()}
          onChangeSettings={() => {
            setOpenSettingsAfterReset(true);
            sock.newSession();
          }}
          onEnd={() => { sock.leave(); onLeave(); }}
        />
        <ProgressionToast toast={xpToast} />
        <ToastQueue toasts={toastQueue.toasts} onDismiss={toastQueue.dismiss} />
      </>
    );
  }

  // First-round vote ("the tavern stirs"). Server enters this state when
  // the host hits Start in a vote-mode lobby; resolves to startGame once
  // the timer expires or every live player has voted.
  if (lobby?.state === 'voting') {
    return (
      <>
      {escapeButton}
      <main className="room-screen">
        <TavernBackdrop active={true} />
        <RoomChrome
          lobby={lobby}
          inviteUrl={inviteUrl}
          copied={copied}
          onCopy={() => copy(inviteUrl)}
          onLeave={() => { sock.leave(); onLeave(); }}
          onOpenSettings={() => setSettingsOpen(true)}
          soundOn={soundOn}
          onToggleSound={() => { audio.unlock(); audio.prefs.toggleMute(); setSoundOn(!audio.prefs.get().muted); feedbackPrefs.set({ sound: !audio.prefs.get().muted }); }}
          charId={character.charId}
          xpRefreshKey={xpRefreshKey}
          xpBoost={xpBoost}
          dailyStreak={dailyStreak}
          lanterns={lanternBalance}
        />
        <MatchStartVotePane
          lobby={lobby}
          vote={sock.vote}
          games={games}
          myCharId={character.charId}
          isHost={isHost}
          onVote={sock.voteCast}
          onSkip={sock.skipRoundEnd}
          onInspectPlayer={setProfileCharId}
        />
        <ToastQueue toasts={toastQueue.toasts} onDismiss={toastQueue.dismiss} />
        <SettingsPanel
          open={settingsOpen}
          isHost={isHost}
          settings={lobby.settings}
          games={games}
          playerCount={lobby.players.filter((p) => !p.isSpectator).length}
          onClose={() => setSettingsOpen(false)}
          onChange={sock.setSettings}
        />
      </main>
      </>
    );
  }

  // Game in progress
  if (lobby?.state === 'playing' && sock.currentMatch) {
    const activeGame = games.find((g) => g.id === sock.currentMatch!.gameId) ?? games.find((g) => g.baseGameId === sock.currentMatch!.gameId);
    return (
      <>
      {escapeButton}
      <main className="play-screen">
        <header className="play-header">
          {/* §1b in-play chrome: room code is hidden during play (it stays
              visible in waiting/voting/roundEnd via RoomChrome). Show the
              game name instead, plus the ⛶ re-enter pill when the user
              backed out of fullscreen (ESC / back-swipe). */}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {!fsActive && !isIos() && fullscreenSupported() && (
              <button
                type="button"
                className="fs-pill"
                onClick={() => void enterGameFullscreen(gameOrientation(sock.currentMatch!.gameId))}
                aria-label="Re-enter fullscreen"
                title="Fullscreen"
              >
                ⛶
              </button>
            )}
            <span className="muted">{activeGame?.name ?? gameName(sock.currentMatch.gameId) ?? ''}</span>
          </span>
          <EventModeBadge mode={lobby.settings.eventMode} />
        </header>
        {/* Tutorial banner removed (2026-05-12): UniversalMatchIntro already
            shows headline + tip during the pre-round phase; the persistent
            in-game card was covering game canvas info. */}
        {/* Wooden table-edge frame around the canvas — per docs/ui/room-screens.md §10 (GS1). */}
        <div className="game-stage-frame">
          <GameStage
            key={sock.currentMatch.matchId}
            matchId={sock.currentMatch.matchId}
            gameId={sock.currentMatch.gameId}
            startsAt={sock.currentMatch.startsAt}
            endsAt={sock.currentMatch.endsAt}
            params={sock.currentMatch.params}
            mySlot={(me?.slot ?? 0) as 0 | 1 | 2 | 3}
            players={lobby.players.filter((p) => !p.isSpectator).map((p) => ({ charId: p.charId, name: p.name, icon: p.icon, slot: p.slot, connId: '' }))}
            tickPayload={sock.lastTick?.matchId === sock.currentMatch.matchId ? sock.lastTick.payload : undefined}
            result={sock.lastResult}
            sendInput={sock.sendInput}
            gameMeta={activeGame}
            eventMode={lobby.settings.eventMode}
            {...(activeGame?.inputBindings
              ? { inputBindings: activeGame.inputBindings }
              : {})}
          />
        </div>
        <UniversalMatchIntro
          key={sock.currentMatch.matchId}
          matchId={sock.currentMatch.matchId}
          game={activeGame}
          fallbackGameId={sock.currentMatch.gameId}
          players={lobby.players.filter((p) => !p.isSpectator)}
          startsAt={sock.currentMatch.startsAt}
          countdownSec={lobby.settings.roundCountdownSec}
          isFirstRound={true}
          mySlot={(me?.slot ?? 0) as 0 | 1 | 2 | 3}
        />
        <RotateOverlay gameId={sock.currentMatch.gameId} matchId={sock.currentMatch.matchId} />
        <ReactionsOverlay reactions={sock.reactions} />
        <div className="play-footer">
          <ReactionPicker open={reactionPicker} unlocked={unlockedEmotes} onPick={(e) => { sock.sendReaction(e); setReactionPicker(false); }} onToggle={() => setReactionPicker((v) => !v)} />
        </div>
      </main>
      </>
    );
  }

  if (lobby?.state === 'roundEnd') {
    return (
      <>
      {escapeButton}
      <main className="room-screen">
        <TavernBackdrop active={true} />
        <RoomChrome
          lobby={lobby}
          inviteUrl={inviteUrl}
          copied={copied}
          onCopy={() => copy(inviteUrl)}
          onLeave={() => { sock.leave(); onLeave(); }}
          onOpenSettings={() => setSettingsOpen(true)}
          soundOn={soundOn}
          onToggleSound={() => { audio.unlock(); audio.prefs.toggleMute(); setSoundOn(!audio.prefs.get().muted); feedbackPrefs.set({ sound: !audio.prefs.get().muted }); }}
          charId={character.charId}
          xpRefreshKey={xpRefreshKey}
          xpBoost={xpBoost}
          dailyStreak={dailyStreak}
          lanterns={lanternBalance}
        />
        <PostRoundPane
          lobby={lobby}
          result={sock.lastResult}
          vote={sock.vote}
          games={games}
          myCharId={character.charId}
          isHost={isHost}
          onVote={sock.voteCast}
          onPickHost={(id) => sock.freeLaunch(id) /* same handler also valid for roundEnd host pick */}
          onSkip={sock.skipRoundEnd}
          onInspectPlayer={setProfileCharId}
        />
        <ProgressionToast toast={xpToast} />
        <ToastQueue toasts={toastQueue.toasts} onDismiss={toastQueue.dismiss} />
        <SettingsPanel
          open={settingsOpen}
          isHost={isHost}
          settings={lobby.settings}
          games={games}
          playerCount={lobby.players.filter((p) => !p.isSpectator).length}
          onClose={() => setSettingsOpen(false)}
          onChange={sock.setSettings}
        />
      </main>
      </>
    );
  }

  // Default: waiting
  return (
    <>
    {escapeButton}
    <main className="room-screen">
      <TavernBackdrop active={true} />
      <RoomChrome
        lobby={lobby}
        inviteUrl={inviteUrl}
        copied={copied}
        onCopy={() => copy(inviteUrl)}
        onLeave={() => { sock.leave(); onLeave(); }}
        onOpenSettings={() => setSettingsOpen(true)}
        soundOn={soundOn}
        onToggleSound={() => { audio.unlock(); audio.prefs.toggleMute(); setSoundOn(!audio.prefs.get().muted); feedbackPrefs.set({ sound: !audio.prefs.get().muted }); }}
        charId={character.charId}
        xpRefreshKey={xpRefreshKey}
        xpBoost={xpBoost}
        dailyStreak={dailyStreak}
        lanterns={lanternBalance}
      />

      {/* One-time iOS PWA nudge — waiting room only, never during play.
          iPhone Safari has no Fullscreen API; Add-to-Home-Screen is the
          only path to edge-to-edge play there (§1b). */}
      <IosInstallNudge />

      {lobby && (
        <>
          <section className="room-seats">
            {[0, 1, 2, 3].map((slot) => {
              const p = lobby.players.find((pl) => pl.slot === slot && !pl.isSpectator);
              if (!p) {
                return (
                  <div key={slot} className="seat empty">
                    <span className="seat-lamp" aria-hidden="true" />
                    <div className="seat-num muted">{slot + 1}</div>
                    <div className="muted">empty</div>
                  </div>
                );
              }
              const titleClass = titleFlair(p.title);
              const seat = p.seatCosmetics ?? { border: 'none', background: 'plain', particle: 'none', bgHue: null };
              const bgHue = (seat as { bgHue?: number | null }).bgHue ?? null;
              const hueProps = bgHue !== null && bgHue !== undefined
                ? { 'data-bg-hue': String(bgHue), style: { ['--seat-bg-h' as string]: String(bgHue) } as React.CSSProperties }
                : {};
              return (
                <div
                  key={slot}
                  className={`seat seat-stool seat-border-${seat.border} seat-bg-${seat.background} seat-particle-${seat.particle}${p.ready ? ' ready' : ''}${p.isHost ? ' host' : ''}${p.isDisconnected ? ' offline' : ''} ${titleClass}`}
                  {...hueProps}
                >
                  <span className="seat-lamp" aria-hidden="true" />
                  <IconAvatar icon={p.icon} size={64} alt={p.name} />
                  <div className="seat-name">{p.name}</div>
                  {p.title && p.title !== 'Tavern Regular' && (
                    <div className={`seat-title ${titleRarityClass(p.title)}`}>{p.title}</div>
                  )}
                  <div className="seat-tags">
                    {p.isHost && <span className="tag host">host</span>}
                    {p.lagMs > 3000 && <span className="tag muted" title={`${Math.round(p.lagMs)}ms lag`}>📶</span>}
                    {p.ready ? <span className="tag ready">ready</span> : <span className="tag muted">…</span>}
                    {p.isDisconnected && <span className="tag muted">reconnecting…</span>}
                    <span className="tag muted">{p.lobbyScore}</span>
                  </div>
                  <div className="seat-status-line">
                    {p.isDisconnected ? 'reconnecting' : p.ready ? 'ready to play' : 'waiting'}
                  </div>
                  <div className="seat-actions">
                    <button type="button" className="link" onClick={() => setProfileCharId(p.charId)}>profile</button>
                    {isHost && p.charId !== character.charId && (
                      <button type="button" className="link kick-link" onClick={() => sock.kick(p.charId)}>kick</button>
                    )}
                  </div>
                </div>
              );
            })}
          </section>
          {/* Ready / Start row — placed FIRST so it's the primary
              call to action and visible above the fold on mobile.
              Per docs/ui/room-screens.md §4.5/§4.6 (W10/W11). */}
          {lobby.settings.mode !== 'free' && (
            <section className="room-controls room-controls-themed">
              <button
                type="button"
                className={`tavern-action tavern-action-ready${me?.ready ? ' is-ready' : ''}`}
                disabled={!me}
                onClick={() => {
                  // §1b: fullscreen MUST be requested inside the user
                  // gesture. Enter when raising the tankard (going ready),
                  // not when backing out.
                  if (!me?.ready) void enterGameFullscreen();
                  sock.ready(!me?.ready);
                }}
                aria-label={me?.ready ? 'Mark not ready' : 'Mark ready'}
              >
                <span className="tankard-body" aria-hidden="true">
                  <span className="tankard-fill" />
                  <span className="tankard-foam" />
                  <span className="tankard-band tankard-band-top" />
                  <span className="tankard-band tankard-band-mid" />
                  <span className="tankard-handle" />
                </span>
                <span className="tavern-action-text">
                  <span className="tavern-action-title">
                    {me?.ready ? 'Not ready' : 'Ready'}
                  </span>
                  <span className="tavern-action-hint">
                    {me?.ready ? 'tap to set the tankard down' : 'raise your tankard'}
                  </span>
                </span>
              </button>
              {isHost && (
                <button
                  type="button"
                  className={`tavern-action tavern-action-start${allReady ? ' is-armed' : ''}`}
                  onClick={() => {
                    // §1b: same gesture rule — ring the bell, take the screen.
                    void enterGameFullscreen();
                    sock.start();
                  }}
                  disabled={!allReady}
                  title={allReady ? 'Start the round' : startBlockedReason}
                  aria-label="Start the round"
                >
                  <span className="bell-rig" aria-hidden="true">
                    <span className="bell-rope" />
                    <span className="bell-body" />
                    <span className="bell-clapper" />
                  </span>
                  <span className="tavern-action-text">
                    <span className="tavern-action-title">Start</span>
                    <span className="tavern-action-hint">
                      {allReady ? 'ring the bell to begin' : startBlockedReason}
                    </span>
                  </span>
                </button>
              )}
            </section>
          )}

          {/* Hanging chalkboard table-status — per docs/ui/room-screens.md §4.4 (W9). */}
          <section className="table-chalkboard" aria-live="polite">
            <span className="table-chalkboard-rope table-chalkboard-rope-l" aria-hidden="true" />
            <span className="table-chalkboard-rope table-chalkboard-rope-r" aria-hidden="true" />
            <div className="table-chalkboard-slate">
              <div className="table-chalkboard-line">
                <span className="table-chalkboard-label">Tonight's set:</span>
                <strong>{lobby.settings.mode === 'free' ? 'Free play' : `${lobby.setRoundsPlayed} of ${lobby.settings.roundsPerSet} rounds`}</strong>
              </div>
              <div className="table-chalkboard-line">
                <span className="table-chalkboard-label">Players ready:</span>
                <strong>{lobby.players.filter((p) => p.ready || p.isSpectator).length}/{lobby.players.length}</strong>
              </div>
              <div className="table-chalkboard-line">
                <EventModeBadge mode={lobby.settings.eventMode} />
              </div>
            </div>
          </section>

          {/* TavernFloor with integrated chat + reactions. Per
              docs/ui/tavern-floor-chat.md — the floor is now the
              social heart of the room: speech bubbles for chat,
              single-glyph bubbles for reactions, composer docked at
              the bottom (no mobile scrolling). */}
          <TavernFloor
            players={lobby.players.filter((p) => !p.isSpectator)}
            onInspectPlayer={setProfileCharId}
            chat={sock.chat}
            reactions={sock.reactions}
            {...(lobby.settings.chatEnabled ? { onSendChat: sock.sendChat } : {})}
            {...(lobby.settings.reactionsEnabled ? { onSendReaction: sock.sendReaction } : {})}
            reactionEmojis={[...REACTION_PICKER_BASE, ...unlockedEmotes.filter((e) => REACTION_PICKER_UNLOCKABLE.includes(e))]}
            chatEnabled={lobby.settings.chatEnabled}
            meCharId={character.charId}
          />

          <CollapsibleDailyChallenge>
            <DailyChallengeWidget charId={character.charId} refreshKey={xpRefreshKey} compact />
          </CollapsibleDailyChallenge>

          {sock.error && <div role="alert" className="gate-error" style={{ textAlign: 'center' }}>{sock.error}</div>}

          {lobby.settings.mode === 'free' && (
            <>
              <section className="room-controls">
                {isHost && (
                  <button type="button" className="btn ghost big" onClick={() => { sock.leave(); onLeave(); }}>End session</button>
                )}
              </section>
              <FreeModeGrid lobby={lobby} games={games} onLaunch={sock.freeLaunch} />
            </>
          )}

          {/* Old standalone chat box removed — chat is now integrated
              into TavernFloor via speech bubbles + composer pill.
              Per docs/ui/tavern-floor-chat.md §3.5. */}
        </>
      )}

      <SettingsPanel
        open={settingsOpen}
        isHost={isHost}
        settings={lobby?.settings ?? sockDefaults()}
        games={games}
        playerCount={lobby?.players.filter((p) => !p.isSpectator).length ?? 0}
        onClose={() => setSettingsOpen(false)}
        onChange={sock.setSettings}
      />
      <ProgressionToast toast={xpToast} />
      <ToastQueue toasts={toastQueue.toasts} onDismiss={toastQueue.dismiss} />
      <MeDrawer open={meOpen} onClose={() => setMeOpen(false)} />
      <CharacterProfileModal
        charId={profileCharId}
        games={games}
        editable={profileCharId === character.charId}
        onClose={() => setProfileCharId(null)}
        onUpdated={(c) => { onCharacterUpdated(c); sock.refreshPresence(); }}
      />
    </main>
    </>
  );
}

/** Map a selected title to a CSS flair class so the seat border tells you
 *  at-a-glance who's a Master vs new player. Mastery titles end in
 *  Apprentice / Adept / Master; they're the most-earned and most-displayed
 *  flair, so they get bespoke styling. Streak / global titles share a softer
 *  glow. */
function titleFlair(title: string | null | undefined): string {
  if (!title || title === 'Tavern Regular') return '';
  if (title === 'First Winner') return 'flair-first-win';
  if (title.endsWith(' Master')) return 'flair-master';
  if (title.endsWith(' Adept')) return 'flair-adept';
  if (title.endsWith(' Apprentice')) return 'flair-apprentice';
  if (title === 'Daily Devotee' || title === 'Streaker') return 'flair-streak';
  if (title === 'Undefeated' || title === 'Table Champion' || title === '1000 Club') return 'flair-elite';
  return 'flair-flair';
}

function sockDefaults() {
  return {
    mode: 'vote' as const,
    eventMode: 'standard' as const,
    roundsPerSet: 5,
    winCondition: 'rounds' as const,
    targetScore: 1000,
    allowedGames: [] as string[],
    voteOptions: 3,
    voteTimerSec: 15,
    roundCountdownSec: 3,
    pickWho: 'vote' as const,
    hostRotation: 'fixed' as const,
    latePlayers: 'join' as const,
    chatEnabled: true,
    reactionsEnabled: true,
    pointMultiplier: 1 as const,
    lobbyPersistHours: 4,
    quickRejoinSec: 30,
    showTutorials: true,
  };
}

function EventModeBadge({ mode }: { mode?: import('@tt/lobby').LobbySettings['eventMode'] }) {
  const label = {
    standard: 'Standard Night',
    lightning: 'Lightning Night',
    comeback: 'Comeback Night',
    chaos: 'Chaos Night',
    cozy: 'Cozy Night',
  }[mode ?? 'standard'];
  return <span className={`event-mode-badge mode-${mode ?? 'standard'}`}>{label}</span>;
}

function RoomChrome({
  lobby, inviteUrl, copied, onCopy, onLeave, onOpenSettings, soundOn, onToggleSound,
  charId, xpRefreshKey, xpBoost, dailyStreak, lanterns,
}: {
  lobby: import('@tt/lobby').LobbyView | null;
  inviteUrl: string;
  copied: boolean;
  onCopy: () => void;
  onLeave: () => void;
  onOpenSettings: () => void;
  soundOn: boolean;
  onToggleSound: () => void;
  charId: string;
  xpRefreshKey: number;
  xpBoost: boolean;
  dailyStreak: number;
  /** Phase D — lantern balance pip. Sourced from /api/whoami via the
   *  cached Character; updates on round end (via stats refresh) and on
   *  return from /payment-success (via App.tsx onCharacterRefresh). */
  lanterns?: number;
}) {
  const liveCount = lobby ? lobby.players.filter((p) => !p.isSpectator).length : 0;
  return (
    <header className="room-header">
      <div className="room-identity">
        <h1 className="display room-title">{lobby?.roomCode ?? '…'}</h1>
        <p className="room-status muted">
          {lobby ? (
            <>
              <span>{liveCount}/4 at the table</span>
              <span className="room-mode-badge">{lobby.settings.mode}</span>
            </>
          ) : 'connecting…'}
        </p>
      </div>
      <div className="room-stats-bar">
        <XPBar charId={charId} refreshKey={xpRefreshKey} boost={xpBoost} />
        {dailyStreak > 0 && (
          <span className="streak-ribbon" title={`${dailyStreak}-day streak`}>
            <Flame size={14} />
            <span>{dailyStreak}</span>
          </span>
        )}
        {typeof lanterns === 'number' && (
          <span className="room-lantern-pip" title={`${lanterns.toLocaleString()} lanterns`} aria-label={`${lanterns} lanterns`}>
            <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>🏮</span>
            <span>{lanterns.toLocaleString()}</span>
          </span>
        )}
        <FeaturedChip />
        <BellButton charId={charId} refreshKey={xpRefreshKey} />
      </div>
      <div className="room-actions">
        <button type="button" className="btn ghost icon-btn" onClick={onCopy} aria-label="copy invite link" title="Invite link">
          <Link2 size={16} /><span>{copied ? 'Copied!' : 'Invite'}</span>
        </button>
        <button type="button" className="btn ghost icon-btn" onClick={onOpenSettings} aria-label="settings" title="Settings">
          <SettingsIcon size={16} /><span>Settings</span>
        </button>
        <button type="button" className="btn ghost icon-btn" onClick={onToggleSound} aria-label="toggle sound" title={soundOn ? 'Mute' : 'Unmute'}>
          {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}<span>Sound</span>
        </button>
        <button type="button" className="btn ghost icon-btn leave" onClick={onLeave} aria-label="leave room" title="Leave">
          <LogOut size={16} /><span>Leave</span>
        </button>
      </div>
      <small style={{ display: 'none' }}>{inviteUrl}</small>
    </header>
  );
}

function CollapsibleDailyChallenge({ children }: { children: React.ReactNode }) {
  // Default-collapsed so the daily challenge widget no longer eats vertical
  // space above Ready / Start. Persisted per-session in localStorage.
  const KEY = 'tt:room:dailyExpanded';
  const [open, setOpen] = useState<boolean>(() => {
    try { return localStorage.getItem(KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(KEY, open ? '1' : '0'); } catch { /* */ }
  }, [open]);
  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      style={{ margin: '8px auto 0', maxWidth: 480, textAlign: 'center' }}
    >
      <summary style={{ cursor: 'pointer', color: 'var(--c-muted, #b09a72)', fontSize: 12, listStyle: 'none', padding: '4px 8px' }}>
        ▸ Daily challenge
      </summary>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>{children}</div>
    </details>
  );
}

function FeaturedChip() {
  const featured = featuredGameForWeek();
  return (
    <span className="featured-chip" title={`This week's featured game (${Math.round(FEATURED_XP_MULTIPLIER * 100)}% Season XP)`}>
      <Star size={14} />
      <span>Featured: {prettifyGameId(featured.gameId)}</span>
    </span>
  );
}
function prettifyGameId(id: string): string {
  return id.split('-').map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(' ');
}

function ReactionsOverlay({ reactions }: { reactions: Array<{ id: number; emoji: string }> }) {
  return (
    <div className="reactions-overlay" aria-hidden="true">
      {reactions.map((r, i) => (
        <span key={r.id} className="reaction-pop" style={{ left: `${(i * 13) % 80 + 10}%` }}>{r.emoji}</span>
      ))}
    </div>
  );
}

function ProgressionToast({ toast }: { toast: { matchId: string; xp: RoundXp } | null }) {
  if (!toast) return null;
  const leveled = toast.xp.levelAfter > toast.xp.levelBefore;
  return (
    <div className="toast-stack progression-toast-stack" aria-live="polite">
      <div className="toast ember progression-toast">
        <span className="progression-toast-kicker">{leveled ? 'Level up!' : 'XP earned'}</span>
        <strong>+{toast.xp.total} XP</strong>
        <span>Lv {toast.xp.levelAfter} · {toast.xp.toNext.current}/{toast.xp.toNext.needed}</span>
      </div>
    </div>
  );
}

function ReactionPicker({ open, onPick, onToggle, unlocked }: { open: boolean; onPick: (e: string) => void; onToggle: () => void; unlocked: string[] }) {
  return (
    <div className="reaction-picker">
      <button type="button" className="btn ghost icon-btn" onClick={onToggle} aria-label="reactions" title="Send a reaction">
        <Smile size={16} />
      </button>
      {open && (
        <div className="reaction-menu" role="menu">
          {REACTION_PICKER_BASE.map((e) => (
            <button key={e} type="button" className="reaction-btn" onClick={() => onPick(e)}>{e}</button>
          ))}
          {REACTION_PICKER_UNLOCKABLE.map((e) => {
            const open = unlocked.includes(e);
            return (
              <button
                key={e}
                type="button"
                className={`reaction-btn${open ? '' : ' locked'}`}
                onClick={() => open && onPick(e)}
                disabled={!open}
                title={open ? '' : 'Locked — earn it through play'}
              >
                {open ? e : '🔒'}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

type UnlockHint = NonNullable<import('../../lib/api').CosmeticInventory['unlockHints']>[number];

function collectUnlockedIds(hints: UnlockHint[]): Set<string> {
  const out = new Set<string>();
  for (const h of hints) {
    if (h.unlocked) out.add(`${h.kind}:${h.id}`);
  }
  return out;
}

function rarityForUnlockKind(kind: UnlockHint['kind']): 1 | 2 | 3 | 4 {
  switch (kind) {
    case 'title': return 3;
    case 'seatBorder':
    case 'seatBackground':
    case 'seatParticle': return 3;
    case 'hat':
    case 'body':
    case 'face': return 2;
    default: return 2;
  }
}
function cosmeticSubtitle(kind: UnlockHint['kind']): string {
  switch (kind) {
    case 'title': return 'New title unlocked';
    case 'seatBorder': return 'New seat frame unlocked';
    case 'seatBackground': return 'New seat backdrop unlocked';
    case 'seatParticle': return 'New seat particles unlocked';
    case 'hat': return 'New hat unlocked';
    case 'body': return 'New body unlocked';
    case 'face': return 'New face unlocked';
    case 'hue':
    case 'accentHue': return 'New color unlocked';
    case 'emote': return 'New emote unlocked';
    default: return 'Unlocked';
  }
}
function cosmeticChip(kind: UnlockHint['kind']): string {
  switch (kind) {
    case 'title': return 'Title';
    case 'seatBorder': return 'Frame';
    case 'seatBackground': return 'Backdrop';
    case 'seatParticle': return 'Particles';
    case 'hat': return 'Hat';
    case 'body': return 'Body';
    case 'face': return 'Face';
    case 'hue':
    case 'accentHue': return 'Color';
    case 'emote': return 'Emote';
    default: return 'Cosmetic';
  }
}
function cosmeticIcon(kind: UnlockHint['kind']): string {
  switch (kind) {
    case 'title': return '🏷';
    case 'seatBorder': return '◇';
    case 'seatBackground': return '◯';
    case 'seatParticle': return '✶';
    case 'hat': return '🎩';
    case 'body': return '👤';
    case 'face': return '☻';
    case 'hue':
    case 'accentHue': return '◐';
    case 'emote': return '🎭';
    default: return '✦';
  }
}
